import React, { useMemo } from "react";
import { IProducaoDiariaDTO, ITendenciaDiaDTO } from "../../../types/tendencia";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";

const daysOfWeek = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const CONTINUOUS_CALENDAR_COLORS = [
  "#00B4DB",
  "#00A8D1",
  "#009DC7",
  "#008FBB",
  "#0083B0",
];

const getCalendarCardColor = (value: number, min: number, max: number) => {
  if (max <= min) {
    return CONTINUOUS_CALENDAR_COLORS[CONTINUOUS_CALENDAR_COLORS.length - 1];
  }

  const normalized = Math.min(1, Math.max(0, (value - min) / (max - min)));
  const index = Math.min(
    CONTINUOUS_CALENDAR_COLORS.length - 1,
    Math.round(normalized * (CONTINUOUS_CALENDAR_COLORS.length - 1))
  );

  return CONTINUOUS_CALENDAR_COLORS[index];
};

interface ContinuousCalendarProps {
  data?: IProducaoDiariaDTO[];
}

const buildDateKey = (year: number, month: number, day: number) =>
  `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(
    2,
    "0"
  )}`;

const getDateKey = (value: string | Date) => {
  if (typeof value === "string") {
    const match = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (match) {
      return match[0];
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return buildDateKey(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate()
      );
    }
    return null;
  }

  if (Number.isNaN(value.getTime())) {
    return null;
  }

  return buildDateKey(value.getFullYear(), value.getMonth(), value.getDate());
};

export const ContinuousCalendar: React.FC<ContinuousCalendarProps> = ({
  data,
}) => {
  const dias = data ?? [];
  const month =
    dias?.length > 0 ? dayjs(dias[0]?.data).month() : new Date().getMonth();
  const year =
    dias?.length > 0 ? dayjs(dias[0]?.data).year() : new Date().getFullYear();

  const getAdjustedMonthYear = (
    baseMonth: number,
    baseYear: number,
    offset: number
  ) => {
    if (offset === -1) {
      return baseMonth === 0
        ? { month: 11, year: baseYear - 1 }
        : { month: baseMonth - 1, year: baseYear };
    }

    if (offset === 1) {
      return baseMonth === 11
        ? { month: 0, year: baseYear + 1 }
        : { month: baseMonth + 1, year: baseYear };
    }

    return { month: baseMonth, year: baseYear };
  };

  const calendarWeeks = useMemo(() => {
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const leadingEmpty = firstDayOfWeek;
    const totalDays = leadingEmpty + daysInMonth;
    const trailingEmpty = (7 - (totalDays % 7)) % 7;

    const calendarDays: Array<{ day: number; monthOffset: number }> = [
      ...Array.from({ length: leadingEmpty }, (_, index) => ({
        day: daysInPrevMonth - leadingEmpty + index + 1,
        monthOffset: -1,
      })),
      ...Array.from({ length: daysInMonth }, (_, index) => ({
        day: index + 1,
        monthOffset: 0,
      })),
      ...Array.from({ length: trailingEmpty }, (_, index) => ({
        day: index + 1,
        monthOffset: 1,
      })),
    ];

    const weeks: Array<Array<{ day: number; monthOffset: number }>> = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }

    return weeks;
  }, [month, year]);

  const { totalByDate, minQtdTotal, maxQtdTotal } = useMemo(() => {
    const totals = new Map<string, number>();
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    dias.forEach((dia) => {
      const dateKey = getDateKey(dia.data);
      if (!dateKey) {
        return;
      }

      totals.set(dateKey, dia.qtdTotal);
      min = Math.min(min, dia.qtdTotal);
      max = Math.max(max, dia.qtdTotal);
    });

    return {
      totalByDate: totals,
      minQtdTotal: Number.isFinite(min) ? min : 0,
      maxQtdTotal: Number.isFinite(max) ? max : 0,
    };
  }, [dias]);

  return (
    <div
      className="no-scrollbar calendar-container max-h-full w-fit"
      data-max-total={maxQtdTotal}
      data-min-total={minQtdTotal}
    >
      <div className="flex w-fit flex-col gap-4 px-2">
        {calendarWeeks.map((week, weekIndex) => (
          <div
            className="grid w-fit grid-cols-[repeat(7,90px)] gap-4"
            key={`week-${weekIndex}`}
          >
            {week.map(({ day, monthOffset }, dayIndex) => {
              const adjusted = getAdjustedMonthYear(month, year, monthOffset);
              const dateKey = buildDateKey(adjusted.year, adjusted.month, day);
              const hasQtdTotal = totalByDate.has(dateKey);
              const qtdTotal = totalByDate.get(dateKey);

              return (
                <div
                  key={`day-${adjusted.year}-${adjusted.month}-${day}-${dayIndex}`}
                  className="relative z-10 group h-[75px] w-[90px] rounded-xl font-medium"
                  style={{
                    backgroundColor: hasQtdTotal
                      ? getCalendarCardColor(
                          qtdTotal ?? 0,
                          minQtdTotal,
                          maxQtdTotal
                        )
                      : "#f6f6f6",
                  }}
                >
                  <span
                    className={`absolute rigth-0 top-0 flex size-5 text-[10px] items-center justify-center rounded-full sm:size-6 sm:text-[10px] lg:right-0 lg:top-0 lg:size-8 ${
                      hasQtdTotal ? "text-white" : "text-black"
                    }`}
                  >
                    {day}
                  </span>
                  {hasQtdTotal ? (
                    <span className="absolute bottom-[50%] left-[50%] translate-x-[-50%] translate-y-[50%] text-base text-white">
                      {qtdTotal}
                    </span>
                  ) : (
                    <span className="absolute bottom-[50%] left-[50%] translate-x-[-50%] translate-y-[50%] text-base">
                      -
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-[repeat(7,106px)] mt-4 w-fit mx-auto">
        {daysOfWeek.map((day, index) => (
          <div
            key={index}
            className={twMerge(
              `py-2 text-center text-black text-xs font-medium border-r-2 border-black`,
              index === 0 && "border-l-2"
            )}
          >
            {day}
          </div>
        ))}
      </div>
    </div>
  );
};
